import type { TemplateSummary } from "@tdelivr/api-client/generated/notifications/template.js";
import type { StateClassKey } from "@tdelivr/design-tokens";

import { asDay, type BoardRow } from "./board.ts";

/**
 * What the messages and templates board does to the summaries NTF-07 returns, away from any
 * rendering.
 *
 * Built to `17 WP16 Tenant Admin.dc.html`, page `templates`, against NTF-07
 * `GET /v1/notification-templates` — published by
 * [ADR 0043](../../../docs/adr/0043-a-tenants-notification-templates-are-listed-under-v1-as-identity-and-state-without-their-body.md)
 * because every operation of that service was under `/internal/v1` and its one read took both a
 * `templateKey` and a `version` that this board exists to discover.
 *
 * **The design names the wrong two services for this page, and that is worth stating because a
 * reader following its codes would spend the time before finding out.** Its source chip reads
 * `NTF-02 · NTF-06`: the SMS adapter and the notification-preference service. Measured over the
 * regenerated catalogue, NTF-02 owns SMS send attempts and NTF-06 owns one user's preference per
 * topic; neither holds a template. The service that does is NTF-07, which is what this page reads
 * and what its chip says. Q-149.
 *
 * **One designed row is many published rows.** NTF-07's record is a template *variant* —
 * `(templateKey, locale, channel)`, the triple `one_published_template_per_variant` is unique over
 * — and the design's row is a template, with `Channel` and `Languages` as lists across its
 * variants. So `templateRows` groups, in the order the listing answered, and both list columns are
 * the group's own values rather than a guess.
 *
 * **Two things the design draws have no source and neither is a column.**
 *
 *   - **`sw missing`.** The design badges `Parcel retry scheduled · SMS · WhatsApp · en` as
 *     `caution · sw missing`, and its `Missing a language · 2 · Swahili` tile counts the same
 *     thing. Nothing on this platform publishes which languages a tenant requires: a template has
 *     the locales somebody created it in, and "missing" is a comparison against a set nobody holds.
 *     Q-152. The board says which languages a template has and never which it lacks.
 *   - **`Delivery failures · 0.4% · today`.** That is NTF-01's and NTF-02's send outcome, not a
 *     template's, and neither publishes a rate. It is one of the four tiles, all four of which this
 *     board does not draw.
 *
 * **`WhatsApp` is not a channel this platform models.** NTF-07's channel enumeration is `sms`,
 * `email`, `push` and `in_app`, which is also what its table's own `CHECK` admits, so the design's
 * `SMS · WhatsApp` cell cannot be produced and no row will ever say WhatsApp. Q-152 records it
 * beside the language absence, because both are the same shape — a vocabulary the design assumes
 * and the platform does not hold.
 */

/**
 * A channel as the design spells it.
 *
 * Its own cells are `push · SMS`, `email` and `SMS · WhatsApp`, so it writes each channel the way
 * that channel is ordinarily written rather than in one case — `SMS` in capitals because it is an
 * initialism, `push` and `email` in lower case because they are words. `in_app` is the one of
 * NTF-07's four the design never draws, because its `templates` page has no in-app row; it is
 * written as `in-app`, which is the same transformation the other three get.
 */
const CHANNEL_WORD: Readonly<Record<string, string>> = {
  sms: "SMS",
  email: "email",
  push: "push",
  in_app: "in-app",
};

export function channelWord(channel: string): string {
  return CHANNEL_WORD[channel] ?? channel;
}

/** Every variant of one template, in the order the listing answered them. */
export interface TemplateGroup {
  readonly templateKey: string;
  readonly variants: readonly TemplateSummary[];
}

/**
 * Group the variants into the rows the design draws, keeping the listing's order.
 *
 * The listing is ordered by `(templateKey, locale, channel)`, so a template's variants arrive
 * together and the first appearance of each key is the row's place. Re-sorting here would replace
 * the service's order with this board's, which `board.ts` is explicit that `default` must not do.
 */
export function templateGroups(templates: readonly TemplateSummary[]): readonly TemplateGroup[] {
  const groups = new Map<string, TemplateSummary[]>();
  for (const template of templates) {
    const held = groups.get(template.templateKey);
    if (held === undefined) groups.set(template.templateKey, [template]);
    else held.push(template);
  }
  return [...groups.entries()].map(([templateKey, variants]) => ({ templateKey, variants }));
}

/** The design's `Channel` column: every channel this template has a variant for, deduplicated. */
export function channelsText(group: TemplateGroup): string {
  return [...new Set(group.variants.map((variant) => channelWord(variant.channel)))].join(" · ");
}

/** The design's `Languages` column: every locale this template has a variant for. */
export function languagesText(group: TemplateGroup): string {
  return [...new Set(group.variants.map((variant) => variant.locale))].join(" · ");
}

/**
 * The state class and the word.
 *
 * `status` is published per variant and a template is a set of them, so the group's state is the
 * best state any of its variants is in — which is the question an administrator is asking: is this
 * message being sent? One of the design's two words is reachable, `live`; the other, `sw missing`,
 * is Q-152.
 */
export function stateOf(group: TemplateGroup): {
  classKey: StateClassKey;
  label: string;
  title?: string;
} {
  const statuses = new Set(group.variants.map((variant) => variant.status));
  if (statuses.has("published")) {
    return {
      classKey: "done",
      label: "live",
      ...(statuses.has("draft")
        ? {
            title:
              "At least one variant of this template is published, and at least one is still a draft. A draft is not sent: NTF-07 renders the published version of the variant it is asked for.",
          }
        : {}),
    };
  }
  if (statuses.has("draft")) {
    return {
      classKey: "waiting",
      label: "draft",
      title:
        "No variant of this template is published, so nothing is sent from it. A render of an unpublished template is refused.",
    };
  }
  return {
    classKey: "closed",
    label: "retired",
    title: "Every variant of this template is retired. Its history stays readable.",
  };
}

/** One row of the templates board: one template, across its variants. */
export function templateRow(group: TemplateGroup): BoardRow {
  const created = group.variants
    .map((variant) => variant.createdAt)
    .reduce((earliest, instant) => (instant < earliest ? instant : earliest));
  const changed = group.variants
    .map((variant) => variant.updatedAt)
    .reduce((latest, instant) => (instant > latest ? instant : latest));
  return {
    id: group.templateKey,
    cells: [
      /*
       * The key, in Geist Mono, where the design writes a name.
       *
       * `Driver arriving`, `Payment outcome unknown`, `Receipt`. NTF-07 holds no human label for a
       * template: `template_key` is `^[a-z][a-z0-9_.-]{1,127}$` and is the only name a template
       * has. The configuration board records the same absence from its own side and the decision is
       * the same one — the key is drawn as a key rather than title-cased into a sentence this
       * console invented. Q-152.
       */
      { text: group.templateKey, mono: true },
      { text: channelsText(group) },
      { text: languagesText(group) },
    ],
    state: stateOf(group),
    detail: group.variants.map((variant) => ({
      label: `${variant.locale} · ${channelWord(variant.channel)}`,
      value: `v${String(variant.version)} · ${variant.status}`,
    })),
    history: [
      { label: "Created", value: asDay(created) },
      { label: "Last changed", value: asDay(changed) },
      {
        label: "Variants",
        value: String(group.variants.length),
      },
    ],
    // The design's own form icon for this page, which is where its detail panel takes the glyph.
    glyph: "sms",
  };
}

/** The states this listing can actually answer, in the order `stateOf` decides them. */
export const TEMPLATE_STATES: readonly string[] = ["live", "draft", "retired"];

/** Which templates a chosen state leaves on the board. Client-side: the listing has no filter. */
export function filterTemplates(
  groups: readonly TemplateGroup[],
  state: string,
): readonly TemplateGroup[] {
  if (state === "") return groups;
  return groups.filter((group) => stateOf(group).label === state);
}
